import type { Module } from "vuex";
import type {
  WalletState,
  Order,
  Trade,
  Exchange,
  WalletMoneyItem,
  WalletRateItem,
  WalletStockPosition,
  WalletIncomeItem,
  WalletTransactionItem,
} from "@/features/wallet/types";

import type { RootState } from "@/store/types";

export const getDefaultWalletState = (): WalletState => ({
  // raw get_wallet payload
  ec: 0,
  ts: 0,
  m: [] as WalletMoneyItem[],
  r: [] as WalletRateItem[],
  s: [] as WalletStockPosition[],
  i: [] as WalletIncomeItem[],
  t: [] as WalletTransactionItem[],

  // normalized collections
  exchanges: [] as Exchange[],
  orders: [] as Order[],
  trades: [] as Trade[],

  // selections
  selectedExchangeId: null,
  selectedOrderId: null,
  selectedOrder: null,
  selectedTradeId: null,
  selectedTrade: null,
});

const state: WalletState = getDefaultWalletState();

const wallet: Module<WalletState, RootState> = {
  namespaced: true,
  state,

  getters: {
    exchanges: (s): Exchange[] => s.exchanges,
    orders: (s): Order[] => s.orders,
    trades: (s): Trade[] => s.trades,

    orderById:
      (s) =>
      (id: string): Order | null => {
        const target = String(id);
        const found = s.orders.find((o) => {
          const rec = o as unknown as Record<string, unknown>;
          const oid =
            (rec.oid as string | number | undefined) ??
            (rec.order_id as string | number | undefined) ??
            "";
          return String(oid) === target;
        });

        return found ?? null;
      },

    tradeById:
      (s) =>
      (id: string): Trade | null => {
        const target = String(id);

        // 1) Try direct match in the trades collection
        const fromTrades = s.trades.find((t) => {
          const rec = t as unknown as Record<string, unknown>;
          const candidateKeys = ["trid", "tid", "trade_id", "oid", "order_id", "id"] as const;

          for (const key of candidateKeys) {
            const v = rec[key];
            if (
              (typeof v === "string" || typeof v === "number") &&
              String(v) === target
            ) {
              return true;
            }
          }

          return false;
        });

        if (fromTrades) {
          return fromTrades;
        }

        // 2) Fallback: scan wallet positions (s.s) and their nested trades (t)
        if (Array.isArray(s.s)) {
          for (const pos of s.s) {
            const position = pos as WalletStockPosition;
            const nestedTrades = Array.isArray(position.t)
              ? (position.t as unknown[])
              : [];

            for (const raw of nestedTrades) {
              // Merge basic instrument data from the position onto the trade
              const enriched = {
                ...(raw as Record<string, unknown>),
                sc:
                  (raw as Record<string, unknown>).sc ??
                  (position as Record<string, unknown>).sc,
                cc:
                  (raw as Record<string, unknown>).cc ??
                  (position as Record<string, unknown>).cc,
                xc:
                  (raw as Record<string, unknown>).xc ??
                  (position as Record<string, unknown>).xc ??
                  (position as Record<string, unknown>).xcode,
                nEN:
                  (raw as Record<string, unknown>).nEN ??
                  (position as Record<string, unknown>).nEN,
                nBG:
                  (raw as Record<string, unknown>).nBG ??
                  (position as Record<string, unknown>).nBG,
                stid:
                  (raw as Record<string, unknown>).stid ??
                  (position as Record<string, unknown>).stid,
              } as unknown as Trade;

              const rec = enriched as unknown as Record<string, unknown>;
              const candidateKeys = [
                "trid",
                "tid",
                "trade_id",
                "oid",
                "order_id",
                "id",
              ] as const;

              for (const key of candidateKeys) {
                const v = rec[key];
                if (
                  (typeof v === "string" || typeof v === "number") &&
                  String(v) === target
                ) {
                  return enriched;
                }
              }
            }
          }
        }

        return null;
      },
  },

  mutations: {
    resetWalletState(s) {
      Object.assign(s, getDefaultWalletState());
    },
    setSelectedOrderId(s, orderId: string | null) {
      s.selectedOrderId = orderId;
    },
    setSelectedOrder(s, order: Order | null) {
      s.selectedOrder = order;
    },
    setSelectedTradeId(s, tradeId: string | null) {
      s.selectedTradeId = tradeId;
    },
    setSelectedExchangeId(s, exchangeId: string | null) {
      s.selectedExchangeId = exchangeId;
    },
    setSelectedTrade(s, trade: Trade | null) {
      s.selectedTrade = trade;
    },
    setOrders(s, orders: Order[] | unknown) {
      s.orders = Array.isArray(orders) ? (orders as Order[]) : [];
    },
    upsertOrder(s, order: Order | unknown) {
      if (!order) {
        return;
      }

      const incoming = order as Order;
      const rec = incoming as unknown as Record<string, unknown>;
      const orderId = rec.oid ?? rec.order_id ?? rec.id ?? rec.orderId;
      if (orderId == null) {
        return;
      }

      const target = String(orderId);
      const index = s.orders.findIndex((o) => {
        const existing = o as unknown as Record<string, unknown>;
        const existingId = existing.oid ?? existing.order_id ?? existing.id ?? existing.orderId;
        return existingId != null && String(existingId) === target;
      });

      if (0 <= index) {
        s.orders[index] = {
          ...s.orders[index],
          ...incoming,
        };
      } else {
        s.orders.push(incoming);
      }
    },
    setTrades(s, trades: Trade[] | unknown) {
      s.trades = Array.isArray(trades) ? (trades as Trade[]) : [];
    },

    // Accept a raw wallet payload (API/SSE) and normalize into module state.
    setWallet(s, payload: unknown) {
      const p = (payload ?? {}) as Record<string, unknown>;

      // Raw fields coming from get_wallet
      const ec = p.ec;
      const ts = p.ts;
      s.ec = typeof ec === "number" ? ec : 0;
      s.ts = typeof ts === "number" ? ts : 0;

      const m = p.m as unknown;
      const r = p.r as unknown;
      const sPositions = p.s as unknown;
      const i = p.i as unknown;
      const t = p.t as unknown;

      s.m = Array.isArray(m) ? (m as WalletMoneyItem[]) : [];
      s.r = Array.isArray(r) ? (r as WalletRateItem[]) : [];
      s.s = Array.isArray(sPositions) ? (sPositions as WalletStockPosition[]) : [];
      s.i = Array.isArray(i) ? (i as WalletIncomeItem[]) : [];
      s.t = Array.isArray(t) ? (t as WalletTransactionItem[]) : [];

      // Optional normalized collections (for other endpoints / SSE)
      const exchanges = (p.exchanges ?? p.ex ?? []) as unknown;
      const orders = (p.orders ?? p.ord ?? p.available_orders ?? []) as unknown;
      const trades = (p.trades ?? p.trd ?? p.executed_trades ?? []) as unknown;

      if (Array.isArray(exchanges)) {
        s.exchanges = exchanges as Exchange[];
      } else if (!s.exchanges) {
        s.exchanges = [];
      }

      if (Array.isArray(orders)) {
        s.orders = orders as Order[];
      } else if (!s.orders) {
        s.orders = [];
      }

      if (Array.isArray(trades)) {
        s.trades = trades as Trade[];
      } else if (!s.trades) {
        s.trades = [];
      }
    },
  },
};

export default wallet;
